<template>
<div class="tnm-project-list-center">
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="計畫項目清單"
      subtitle="建立與管理各項計畫項目"
      icon="tabler-clipboard-list"
      :count="filteredProjects.length"
    >
      <template #actions>
        <VBtn color="white" variant="flat" class="text-primary font-weight-bold" to="/tca/tnm/ProjectCreationAndPublication">
          <VIcon icon="tabler-plus" class="mr-1" />建立
        </VBtn>
      </template>
    </ListPageHeader>

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" sm="6" md="3">
          <v-text-field label="申請單號" v-model="filters.applyNo" placeholder="輸入單號搜尋" prepend-inner-icon="tabler-file-search" clearable></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field label="計畫項目名稱" v-model="filters.projectName" prepend-inner-icon="tabler-search" clearable></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <AppDateTimePicker v-model="filters.publishStartDate" placeholder="發文日期起" clearable
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <AppDateTimePicker v-model="filters.publishEndDate" placeholder="發文日期訖" clearable
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
           <AppInput
            v-model="filters.status"
            type="select"
            :remoteItem="deSelectSelect"
            :remoteItemParams="{ tabId}"
            item-title="label"
            item-value="value"
            placeholder="請選擇狀態"
            :disabled="false"
            :loading="false"
            clearable
          />
        </v-col>
      </v-row>
    </v-card-item>

    <InteractiveDataTable
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      :items-length="filteredProjects.length"
      :items="pagedItems"
      :headers="headers"
      :loading="loading"
      :row-clickable="false"
      :row-props="rowProps"
      empty-text="尚無計畫項目"
      empty-subtitle="可調整上方篩選條件後再查詢，或點右上角「建立」新增"
      empty-icon="tabler-clipboard-off"
      :items-per-page-options="[
        {value: 10, title: '10'},
        {value: 25, title: '25'},
        {value: 50, title: '50'},
        {value: 100, title: '100'},
      ]"
    >
      <template v-slot:item.projectId="{ item }">
        <div class="d-inline-flex align-center flex-nowrap">
          <v-btn
            @click.stop="edit(item)"
            color="primary"
            variant="text"
            class="pa-0 idt-link font-weight-bold"
          >
            {{ toText(item.project.projectId) }}
          </v-btn>
          <v-tooltip location="top" text="複製單號">
            <template #activator="{ props: tipProps }">
              <v-btn
                v-bind="tipProps"
                variant="text"
                icon
                size="x-small"
                density="compact"
                class="ms-1"
                @click.stop="notifyCopyDocNo(item.project.projectId)"
              >
                <VIcon icon="tabler-copy" size="18" />
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.publishDate="{ item }">
        <span v-if="item.project?.publishDate" class="d-inline-flex align-center text-medium-emphasis text-no-wrap">
          <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
          {{ westernToRoc(item.project?.publishDate,'-','-') }}
        </span>
        <span v-else class="text-disabled">—</span>
      </template>
      <template v-slot:item.status="{ item }">
        <Status
          :status="item.project.status"
          :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']"
          :is-green="['PreApproved']"
          :is-warning="['Draft']"
          :shared-table-id="tabId"
          btn-class="x-large"
          font-class="18px"
          :status-config="getStatusData"
        />
      </template>
    </InteractiveDataTable>
  </v-card>
</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue"
import { useRouter } from "vue-router"
import Status from './StatusComponenet.vue'
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'
definePage({
    meta: {
        action: 'manage', //固定為 manage
        subject: 'PROJECTLIST', //系統代碼(系統授權設定中的系統代碼)
    },
})

// import { provide } from 'vue'
const router = useRouter()
const filters=reactive( {
  applyNo: '',
  projectName: '',
  publishStartDate: '',
  publishEndDate:'',
  status: '',
})
const statusOptions = reactive(['進行中', '草稿'])
const headers= reactive([
  { title: '申請單號', key: 'projectId', icon: 'tabler-file-text' },
  { title: '計畫項目名稱', key: 'project.projectTitle', icon: 'tabler-clipboard-text' },
  { title: '公文號', key: 'project.docNo', icon: 'tabler-hash' },
  { title: '公文發文日期', key: 'publishDate', icon: 'tabler-calendar-event' },
  { title: '狀態', key: 'status', icon: 'tabler-flag' },
])
const projects= reactive([])
const page=ref(1)
const itemsPerPage = ref(100)
const pageCount=ref(1)
const loading = ref(false)

// 申請單號為前端即時搜尋（後端清單 API 無此參數）
const filteredProjects = computed<any[]>(() => {
  const kw = (filters.applyNo ?? '').toString().trim().toLowerCase()
  if (!kw) return projects

  return projects.filter(item => (item?.project?.projectId ?? '').toString().toLowerCase().includes(kw))
})

// client 端分頁切片（資料一次抓回，前端分頁）
const pagedItems = computed(() => {
  if (itemsPerPage.value === -1) return filteredProjects.value
  const start = (page.value - 1) * itemsPerPage.value

  return filteredProjects.value.slice(start, start + itemsPerPage.value)
})

// 搜尋條件變動後回到第 1 頁
watch(() => filteredProjects.value.length, () => {
  page.value = 1
})

const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow(
  'tnm_project_list_center_last_clicked',
  (item: any) => item?.project?.projectId,
)

const listener = (event)  =>{
  console.log(`news: ${event}`)
}

const getStatusColor = (status) =>{
  if (status === '進行中') return { color: 'success' }
  return { color: '' }
}

const edit = (item) =>{
  setLastClickedId(item?.project?.projectId)
  router.push({ path: '/tca/tnm/ProjectCreationAndPublication', query: { applicationNumber: item.project.projectId } })
}

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  $swal({
    icon: ok ? 'success' : 'error',
    title: ok ? '已複製到剪貼簿' : '複製失敗',
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: ok ? 1500 : 2000,
  })
}

const toWestern = (date: string, splitFormat: string, format: string) => {
  const dateParts = date.split(splitFormat);
  let y = Number(dateParts[0])+1911
  return `${y}${format}${dateParts[1]}${format}${dateParts[2]}`
}  
const fetchData =async() =>{
  let startDate = filters.publishStartDate?toWestern(filters.publishStartDate,'-','-'):null
  let endDate = filters.publishEndDate?toWestern(filters.publishEndDate,'-','-'):null
  let status = filters.status?[filters.status]:null
  loading.value = true
  page.value = 1
  try {
    const response = await $api('/tca/projectList/get', {
      method: 'POST',
      body: {
        projectTitleLike: filters.projectName,
        publishStartDate: startDate,
        publishEndDate: endDate,
        statusEqual: status,
        onlyDept:false,
      },
    });

    // 根据响应做一些处理
    if (response.length>0) {
      projects.splice(0, projects.length, ...response)
    } else {
      // 处理错误情况
      projects.splice(0, projects.length)
    }
  } catch (error) {
    console.error('Error during fetch:', error);
    projects.splice(0, projects.length)
  } finally {
    loading.value = false
  }
}

  const tabId = ref('ProjectCreationAndPublication')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))
watch(filters,()=>{
  fetchData()
})
onMounted(()=>{
  fetchData()
})
</script>

<style scoped>
.ml-2 {
  margin-left: 8px;
}
</style>
